import { CampaignType_Enum as CampaignType } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { SortDirection } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import {
  useGetCampaignsSummary,
  useGetUsage,
  flattenLocationUsages,
  sortCampaigns,
} from '@frontend/api-bulk-messaging';
import { useAppScopeStore } from '@frontend/scope';
import { CampaignGroup } from '../types';

type Props = {
  campaignType: CampaignType;
  selectedLocationIds: string[];
  selectedSort: SortDirection;
  selectedYear: number;
};

export const useCampaignGroups = ({ campaignType, selectedLocationIds, selectedSort, selectedYear }: Props) => {
  const { selectedOrgId: orgId } = useAppScopeStore();

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const {
    data: campaignsSummary = [],
    isLoading: isCampaignsSummaryLoading,
    isError: isCampaignsSummaryError,
  } = useGetCampaignsSummary({
    orgId,
    locationIds: selectedLocationIds,
    sort: selectedSort,
    types: [campaignType],
    year: selectedYear,
  });

  const {
    data: locationUsages,
    isLoading: isLocationUsagesLoading,
    isError: isLocationUsagesError,
  } = useGetUsage({
    orgId,
    locationIds: selectedLocationIds,
    type: campaignType,
    year: selectedYear,
  });

  const campaignGroups: CampaignGroup[] = [];

  for (const usage of flattenLocationUsages(locationUsages)) {
    const existingGroupIndex = campaignGroups.findIndex((group) => group.month === usage.month);
    if (existingGroupIndex === -1) {
      campaignGroups.push({
        month: usage.month,
        campaigns: [],
        usage,
      });
    } else if (campaignGroups[existingGroupIndex]) {
      const existingGroup = campaignGroups[existingGroupIndex];
      campaignGroups[existingGroupIndex] = {
        ...existingGroup,
        usage: {
          month: existingGroup.usage.month,
          error: existingGroup.usage.error + usage.error,
          scheduled: existingGroup.usage.scheduled + usage.scheduled,
          allotment: existingGroup.usage.allotment + usage.allotment,
          sent: existingGroup.usage.sent + usage.sent,
        },
      };
    }
  }

  for (const campaign of campaignsSummary) {
    const month = (campaign.startSendAt ? new Date(campaign.startSendAt) : new Date()).getMonth() + 1;
    const existingGroupIndex = campaignGroups.findIndex((group) => group.month === month);
    if (existingGroupIndex === -1) {
      campaignGroups.push({
        month,
        campaigns: [campaign],
        usage: {
          month,
          error: 0,
          scheduled: 0,
          allotment: 0,
          sent: 0,
        },
      });
    } else if (campaignGroups[existingGroupIndex]) {
      const existingGroup = campaignGroups[existingGroupIndex];
      campaignGroups[existingGroupIndex] = {
        ...existingGroup,
        campaigns: [...existingGroup.campaigns, campaign],
      };
    }
  }

  const sortedCampaignGroups: CampaignGroup[] = campaignGroups
    .filter(
      (campaignGroup) =>
        (selectedYear === currentYear && campaignGroup.month === currentMonth) || campaignGroup.campaigns.length !== 0
    )
    .map((group) => ({
      ...group,
      campaigns: sortCampaigns(group.campaigns),
    }))
    .sort((a, b) => (selectedSort === SortDirection.ASCENDING ? a.month - b.month : b.month - a.month));

  const isLoading = isCampaignsSummaryLoading || isLocationUsagesLoading;
  const isError = isCampaignsSummaryError || isLocationUsagesError;

  return {
    campaignGroups: sortedCampaignGroups,
    isError,
    isLoading,
  };
};
